// DOCS → THE ORIGIN MODAL — "show me the sentence this came from", in place.
//
// A feature's origin is a claim: this requirement came from THAT material, at
// THAT character. The claim is only worth something if a reader can check it in
// one click, and the check has to be cheap — leaving the Features tab to hunt
// through a twelve-thousand-character document, then finding the way back, is
// expensive enough that nobody does it twice. So the origin opens HERE: the
// material's extracted text, scrolled to the quoted place, with the quote lit
// up. The Materials deep-link is still one click away (secondary action) for
// the reader who wants the tab itself.
//
// WHY THE EXTRACTED TEXT AND NOT THE FILE. `position` is computed backend-side
// against `requirement_source.text` (docs-model.quotePosition) — the words the
// review actually read. Rendering the .docx would be rendering a DIFFERENT
// string, in which the offset means nothing. The original is offered as a
// download beside it, not as the thing the highlight lives in.
//
// PAYLOAD. The text is fetched when the modal opens, for ONE material, through
// the same GET /api/docs/materials/:id the Materials tab expands with. The list
// endpoints stay preview-sized; a 26k-character document is never in a list
// payload because a row might be clicked.
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Loader2, X, ExternalLink, Download, FileText, AlertTriangle } from "lucide-react";
import { fetchData } from "../../fetchapi/fetchapi";
import { locateQuote } from "./locate";

// A signed GET whose host is a CONTAINER-side name is not reachable from the
// browser — a link to it is a dead link dressed as a working one. Known local
// limitation of the GET presign path; the honest answer is a disabled button
// that says so, not a tab that fails to load.
const CONTAINER_HOSTS = /^(host\.docker\.internal|minio|s3|.*\.internal)$/i;
function browserReachable(url) {
  try { return !CONTAINER_HOSTS.test(new URL(url).hostname); } catch { return false; }
}

export default function OriginModal({ material, quote, position, version, onClose, onOpenInMaterials }) {
  const [text, setText] = useState(null);
  const [meta, setMeta] = useState(null);
  const [err, setErr] = useState("");
  const [dl, setDl] = useState({ state: "idle", why: "" });
  const bodyRef = useRef(null);
  const markRef = useRef(null);
  const closeRef = useRef(null);
  const id = material?.id ?? null;

  // Esc closes, and focus starts inside the dialog so the keyboard has somewhere
  // to be. Restoring focus on unmount keeps the Features row the reader came
  // from as the place they return to.
  useEffect(() => {
    const was = document.activeElement;
    const onKey = (e) => { if (e.key === "Escape") { e.stopPropagation(); onClose?.(); } };
    document.addEventListener("keydown", onKey, true);
    closeRef.current?.focus();
    return () => {
      document.removeEventListener("keydown", onKey, true);
      if (was && typeof was.focus === "function") was.focus();
    };
  }, [onClose]);

  // ONE material's text, on open. Nothing is prefetched: a features list of
  // forty rows would otherwise carry a quarter of a megabyte of document text
  // nobody asked to read.
  useEffect(() => {
    if (id == null) return undefined;
    let alive = true;
    setText(null); setErr("");
    const q = version ? `?version=${version}` : "";
    fetchData(`docs/materials/${id}${q}`, { method: "GET" })
      .then((r) => { if (!alive) return; setText(String(r?.text ?? "")); setMeta(r || null); })
      .catch((e) => { if (!alive) return; setText(""); setErr(String(e?.message || "Could not read the material.")); });
    return () => { alive = false; };
  }, [id, version]);

  const hit = text == null ? null : locateQuote(text, quote, position);

  // Scroll AFTER paint, not after the state update: the <mark> does not exist
  // until the text has rendered, and scrolling to a node that is not laid out
  // yet silently does nothing — which reads as a link that opened a modal at
  // the top of a document and left the reader to search.
  useLayoutEffect(() => {
    if (text == null) return;
    const el = markRef.current;
    const box = bodyRef.current;
    if (!el || !box) return;
    // scrollIntoView on a nested scroller also scrolls the PAGE behind the
    // dialog; positioning the scroller directly keeps the modal still.
    box.scrollTop = Math.max(0, el.offsetTop - box.clientHeight / 3);
  }, [text, quote, position]);

  const download = useCallback(async () => {
    if (id == null) return;
    setDl({ state: "busy", why: "" });
    try {
      const r = await fetchData(`requirements/sources/${id}/url`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: "{}",
      });
      const url = r?.url;
      if (!url) throw new Error("no url came back");
      if (!browserReachable(url)) {
        setDl({
          state: "unreachable",
          why: "The stored original is signed for an address only the server can reach, so this browser cannot fetch it. Open it from the Materials tab on a deployed environment.",
        });
        return;
      }
      window.open(url, "_blank", "noopener");
      setDl({ state: "idle", why: "" });
    } catch (e) {
      const why = String(e?.message || "could not open the original");
      // A pasted request has no stored file at all — that is a fact about the
      // material, not a failure, so it is said plainly.
      setDl({ state: "unreachable", why: /no stored original/i.test(why) ? "This material was typed, not uploaded — there is no original file to download." : why });
    }
  }, [id]);

  if (id == null) return null;
  const name = material?.name || meta?.name || `material #${id}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      data-testid="origin-modal" role="dialog" aria-modal="true" aria-label={`Origin in ${name}`}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div className="w-full max-w-3xl max-h-[88vh] flex flex-col rounded-xl border border-card-border bg-card-bg p-5 gap-3">
        <div className="flex items-start gap-2">
          <FileText size={18} className="text-nav-active mt-0.5 shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-page-text truncate" title={name} data-testid="origin-modal-title">{name}</div>
            <div className="text-[11px] text-page-muted">
              {position != null ? <>the quoted passage, at character {position}</> : "the quoted passage"}
              {hit && hit.moved ? <span className="text-amber-700 dark:text-amber-400"> · found at {hit.start} — the stored offset was off by {hit.start - position}</span> : null}
              {version ? <span> · materials v{version}</span> : null}
            </div>
          </div>
          <button type="button" ref={closeRef} onClick={() => onClose?.()} data-testid="origin-modal-close"
            aria-label="Close" title="Close (Esc)"
            className="shrink-0 text-page-muted hover:text-page-text bg-transparent border-0 cursor-pointer p-1">
            <X size={16} />
          </button>
        </div>

        {quote ? (
          <blockquote className="border-l-2 border-nav-active pl-2 text-xs text-page-muted italic shrink-0" data-testid="origin-modal-quote">
            "{quote}"
          </blockquote>
        ) : null}

        {err ? <div className="text-xs text-red-600" data-testid="origin-modal-error">{err}</div> : null}

        {text == null ? (
          <div className="flex items-center gap-2 text-sm text-page-muted py-8 justify-center">
            <Loader2 size={16} className="animate-spin" /> reading the material…
          </div>
        ) : (
          <pre ref={bodyRef} data-testid="origin-modal-body"
            className="flex-1 min-h-0 text-[11px] leading-relaxed whitespace-pre-wrap rounded-md p-3 overflow-y-auto font-mono text-page-text"
            style={{ background: "var(--table-stripe)" }}>
            {hit ? (
              <>
                {text.slice(0, hit.start)}
                <mark ref={markRef} data-testid="origin-modal-mark"
                  className="rounded px-0.5 text-page-text"
                  style={{ background: "rgba(245,158,11,.38)", boxShadow: "0 0 0 2px rgba(245,158,11,.38)" }}>
                  {text.slice(hit.start, hit.end)}
                </mark>
                {text.slice(hit.end)}
              </>
            ) : (text || "(no text)")}
          </pre>
        )}

        {text != null && !hit && quote ? (
          <div className="text-[11px] text-amber-700 dark:text-amber-400 inline-flex items-start gap-1.5 shrink-0" data-testid="origin-modal-nohit">
            <AlertTriangle size={12} className="mt-0.5 shrink-0" />
            The quoted words are not in this material's text as it stands now — it
            may have been edited since the review read it.
          </div>
        ) : null}

        <div className="flex flex-wrap items-center gap-2 shrink-0">
          <button type="button" data-testid="origin-modal-open-materials"
            onClick={() => { onClose?.(); onOpenInMaterials?.(id); }}
            className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border border-page-divider bg-transparent cursor-pointer hover:bg-card-border/30 text-page-text">
            <ExternalLink size={12} /> Open in Materials
          </button>
          <button type="button" data-testid="origin-modal-download" onClick={download}
            disabled={dl.state === "busy" || dl.state === "unreachable"}
            title={dl.state === "unreachable" ? dl.why : "Download the original file this text was read from"}
            className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border border-page-divider bg-transparent cursor-pointer hover:bg-card-border/30 text-page-text disabled:opacity-50 disabled:cursor-not-allowed">
            {dl.state === "busy" ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />} Download the original
          </button>
          {dl.state === "unreachable" ? (
            <span className="text-[11px] text-page-muted max-w-md" data-testid="origin-modal-download-why">{dl.why}</span>
          ) : null}
          <span className="ml-auto text-[11px] text-page-muted">Esc to close</span>
        </div>
      </div>
    </div>
  );
}
